import React, { useCallback, useState } from 'react';
import {
  Button,
  FlexRow,
  FlexSpacer,
  IconButton,
  LabeledInput,
  Panel,
  TextInput,
} from '@epam/promo';
import { ReactComponent as closeIcon } from '@epam/assets/icons/common/navigation-close-18.svg';

import {
  resetDraftTaskGroup,
  setDraftTaskGroup,
  setIsDraftTaskGroupValid,
  useTaskGroupsStore,
} from 'store/task-groups';
import { getErrorMessage, getQuestionErrorMessage } from '../taskGroupsHelper';
import { TaskGroupFormProps } from './TaskGroupsForm.model';
import css from './TaskGroupsForm.module.scss';
import { useLeaveTaskGroupsModal } from 'hooks/context/use-leave-task-groups-modal';
import { validateHTML } from 'hooks/helpers/common';
import {
  useModal,
  useTaskGroupsCreateQuery,
  useTaskGroupsUpdateQuery,
} from 'hooks';
import { getOrderBetween } from '@epam/uui-core';

export const TaskGroupsForm = ({ onClose }: TaskGroupFormProps) => {
  const [groupNameError, setGroupNameError] = useState<string | null>(null);
  const [groupQuestionError, setGroupQuestionError] = useState<string | null>(
    null,
  );
  const {
    isDraftTaskGroupValid,
    taskGroups,
    selectedTaskGroup,
    draftTaskGroup,
  } = useTaskGroupsStore((state) => state);
  const { mutate: updateTaskGroup } = useTaskGroupsUpdateQuery();
  const { mutate: createTaskGroup } = useTaskGroupsCreateQuery();
  const leaveTaskGroups = useLeaveTaskGroupsModal();
  const modal = useModal();

  const isEditingMode = !!selectedTaskGroup;

  const createUpdateTaskGroup = useCallback(() => {
    if (isEditingMode) {
      updateTaskGroup({
        id: selectedTaskGroup.id,
        taskGroup: {
          ...selectedTaskGroup,
          ...draftTaskGroup,
        },
      });
    } else {
      const lastTaskGroupOrder = taskGroups.at(-1)?.order ?? null;

      createTaskGroup({
        ...draftTaskGroup,
        order: getOrderBetween(lastTaskGroupOrder, null),
      });
    }
  }, [
    isEditingMode,
    selectedTaskGroup,
    draftTaskGroup,
    taskGroups,
    updateTaskGroup,
    createTaskGroup,
  ]);

  const handleSubmit = useCallback(() => {
    const action = isEditingMode ? 'update' : 'create';

    modal.confirm({
      text: `Are you sure you want to ${action} "${validateHTML(draftTaskGroup.name)}"?`,
      highlights: [`"${validateHTML(draftTaskGroup.name)}"`, action],
      successAction: 'Yes',
      onSuccess: () => {
        createUpdateTaskGroup();
        resetDraftTaskGroup();
        onClose();
      },
    });
  }, [modal, isEditingMode, draftTaskGroup, onClose, createUpdateTaskGroup]);

  const handleClose = useCallback(() => {
    leaveTaskGroups({
      onSuccess: onClose,
      onAbort: onClose,
    });
  }, [leaveTaskGroups, onClose]);

  const handleChangeName = useCallback(
    (newValue: string) => {
      setDraftTaskGroup({
        ...draftTaskGroup,
        name: newValue,
      });

      const message = getErrorMessage(
        selectedTaskGroup?.name ?? '',
        newValue,
        taskGroups,
      );

      if (message) {
        setGroupNameError(message);
        setIsDraftTaskGroupValid(false);
      } else {
        setGroupNameError(null);
        setIsDraftTaskGroupValid(true);
        if (groupQuestionError) {
          setIsDraftTaskGroupValid(false);
        }
      }
    },
    [draftTaskGroup, selectedTaskGroup?.name, groupQuestionError, taskGroups],
  );

  const handleChangeQuestion = useCallback(
    (newValue: string) => {
      setDraftTaskGroup({
        ...draftTaskGroup,
        question: newValue,
      });

      const message = getQuestionErrorMessage(newValue);

      if (message) {
        setGroupQuestionError(message);
        setIsDraftTaskGroupValid(false);
      } else {
        setGroupQuestionError(null);
        setIsDraftTaskGroupValid(true);
        if (groupNameError) {
          setIsDraftTaskGroupValid(false);
        }
      }
    },
    [draftTaskGroup, groupNameError],
  );

  const handleBlurName = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) =>
      handleChangeName(e.target.value.trim()),
    [handleChangeName],
  );

  const handleBlurQuestion = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) =>
      handleChangeQuestion(e.target.value.trim()),
    [handleChangeQuestion],
  );

  return (
    <Panel cx={css.inputPanel}>
      <FlexRow>
        <FlexSpacer />
        <IconButton icon={closeIcon} onClick={handleClose} />
      </FlexRow>
      <LabeledInput
        isRequired
        cx={css.label}
        isInvalid={!!groupNameError}
        label="GROUP NAME"
        validationMessage={groupNameError}>
        <TextInput
          autoFocus
          isInvalid={!!groupNameError}
          placeholder="Add Group name"
          size="48"
          value={draftTaskGroup.name}
          onBlur={handleBlurName}
          onValueChange={handleChangeName}
        />
      </LabeledInput>
      <LabeledInput
        cx={css.label}
        isInvalid={!!groupQuestionError}
        label="USER QUESTION"
        validationMessage={groupQuestionError}>
        <TextInput
          isInvalid={!!groupQuestionError}
          placeholder="Add User question"
          size="48"
          value={draftTaskGroup.question}
          onBlur={handleBlurQuestion}
          onValueChange={handleChangeQuestion}
        />
      </LabeledInput>
      <FlexRow>
        <FlexSpacer />
        <Button
          caption="Save group"
          color="green"
          isDisabled={!isDraftTaskGroupValid}
          onClick={handleSubmit}
        />
      </FlexRow>
    </Panel>
  );
};
